import { isObject } from 'lodash';
import { Spacing as ThemeSpacing } from '@zapier/theme';

export type SpacingAmount = string | number | boolean;
export enum spacingTypes {
  all = 'all',
  bottom = 'bottom',
  horizontal = 'horizontal',
  left = 'left',
  right = 'right',
  top = 'top',
  vertical = 'vertical',
}
export type SpacingType = keyof typeof spacingTypes;

export type SpacingProp = 'margin' | 'padding';
export type SpacingObj = {
  [x in SpacingType]?: SpacingAmount;
};
export type Spacing = SpacingAmount | SpacingObj;
/**
 * Dimensional Tests
 * key: dimension
 * value: tests array in priority order
 */

const dimensions: {
  [x: string]: Array<spacingTypes>;
} = {
  Bottom: [spacingTypes.bottom, spacingTypes.vertical, spacingTypes.all],
  Left: [spacingTypes.left, spacingTypes.horizontal, spacingTypes.all],
  Right: [spacingTypes.right, spacingTypes.horizontal, spacingTypes.all],
  Top: [spacingTypes.top, spacingTypes.vertical, spacingTypes.all],
};

const resolveSpacing = (
  value: string | number | boolean | undefined,
  defaultAmount: string | undefined | null
) => {
  // if true we want to use defaultAmount as the value
  if (value === true)
    return (
      ThemeSpacing[defaultAmount as keyof typeof ThemeSpacing] ||
      defaultAmount ||
      0
    );

  // If false always use 0 as value (using it as a clear)
  if (value === false) return 0;

  // Try to find a theme spacing
  return (
    (value && ThemeSpacing[value as keyof typeof ThemeSpacing]) ||
    value ||
    defaultAmount ||
    0
  );
};

const convertSpacings = (
  spacingDef: SpacingObj,
  defaultAmount: string | undefined | null,
  prop: SpacingProp
) => {
  const spacings = Object.keys(spacingDef);
  return Object.keys(dimensions).reduce((acc, dim) => {
    // look for any spacings that match the tests for the dimension
    const spacing = dimensions[dim].find(test => spacings.includes(test));

    if (spacing) {
      // we found a matching spacing so apply it to the return
      return {
        ...acc,
        [`${prop}${dim}`]: resolveSpacing(spacingDef[spacing], defaultAmount),
      };
    }

    // no matching spacing was found so don't add anything
    return acc;
  }, {});
};
/**
 * Converts spacing definitions to css-in-js spacings.
 */

/** @component */

export const spacingToCss = (prop: SpacingProp) => (
  spacingDef?: Spacing | null,
  defaultAmount: string = '20px'
) => {
  // Just return empty object if the spacing def wasn't supplied
  if (spacingDef === null || spacingDef === undefined) return {};

  if (isObject(spacingDef)) {
    // spacingDef is an object so we can just pass it through
    return convertSpacings(spacingDef, defaultAmount, prop);
  }

  // spacing def is a string / number / boolean
  if (spacingTypes[spacingDef as keyof typeof spacingTypes]) {
    // spacingDef is one of the spacingTypes so use defaultAmount with it
    return convertSpacings(
      {
        [spacingDef as string]: true,
      },
      defaultAmount,
      prop
    );
  } else {
    // spacingDef is an unknown string / number / boolean so use it with 'all'
    return convertSpacings(
      {
        [spacingTypes.all]: spacingDef,
      },
      defaultAmount,
      prop
    );
  }
};
export const marginToCss = spacingToCss('margin');
export const paddingToCss = spacingToCss('padding');
